import fs from 'fs'
import path from 'path'

// How the CLI tells a user to invoke it.
//
// `node_modules/.bin` is on PATH only inside npm's own lifecycle — `npm run`, `npm exec`, `npx` — never in
// an interactive shell. So a bare `flowrunner …` runs only when a GLOBAL install put it on PATH, and a
// project that has the CLI as a dependency and nothing else answers `command not found`. That is the whole
// bug: the hint was written by people who had a global install, for people who do not.
//
// Some terminals muddy this further by injecting `node_modules/.bin` themselves (JetBrains IDEs do), which
// is why "it works for me" is not evidence either way.

/**
 * The invocation for text written to FILES.
 *
 * A README is committed and read on machines other than the one that generated it, so it cannot encode this
 * machine's PATH. `npx` resolves `node_modules/.bin` first and falls back to PATH, so it is right for a
 * local install, a global one, and CI alike.
 */
export const PORTABLE_CLI_COMMAND = 'npx flowrunner'

// npm installs global bins alongside the running node binary, so this one `existsSync` answers "is there a
// global flowrunner" without spawning `npm prefix -g`.
const GLOBAL_BIN_NAMES = process.platform === 'win32'
  ? ['flowrunner.cmd', 'flowrunner.exe', 'flowrunner']
  : ['flowrunner']

let resolved: boolean | undefined

/**
 * Whether a global install has put `flowrunner` on PATH.
 *
 * This is what makes the bare command usable at all — and, with the launcher in `bin/resolve-local.js`,
 * what lets it hand over to the project's own copy.
 */
export function hasGlobalInstall(): boolean {
  if (resolved === undefined) {
    const binDirectory = path.dirname(process.execPath)

    resolved = GLOBAL_BIN_NAMES.some(name => fs.existsSync(path.join(binDirectory, name)))
  }

  return resolved
}

/**
 * The invocation to PRINT, for the person at this keyboard right now.
 *
 * Bare when a global install makes it resolvable, `npx flowrunner` otherwise — so every hint the CLI prints
 * is one the reader can actually paste. Wrong only in the safe direction: a terminal that injects
 * `node_modules/.bin` gets the `npx` form, which still runs. It never prints a bare command that fails.
 *
 * Both forms run the same code: the bare one goes through the global launcher, which delegates to the
 * project's copy, and `npx` resolves that copy directly.
 */
export function cliCommand(): string {
  return hasGlobalInstall() ? 'flowrunner' : PORTABLE_CLI_COMMAND
}

/** Testing seam: the answer is cached because it cannot change within one run. */
export function resetCliCommandCache(): void {
  resolved = undefined
}
